"use client";
import { useRef, useState, useEffect, useLayoutEffect } from "react";
import styles from "./Video.module.scss";
import { useCursorStore } from "../../../store/cursor-store";
import { gsap, ScrollTrigger } from "@/utils/gsap";
import { API_BASE } from "../../../constants/config";

export const Video = ({
  videos = [],
}: {
  videos?: Array<{ type: string; url: string }>;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [videoSrc, setVideoSrc] = useState("");

  const { stopPlaying, startPlaying } = useCursorStore();

  const handleClick = () => {
    if (isPlaying && videoRef.current) {
      videoRef.current.muted = true;
      setIsPlaying(false);
      stopPlaying();
    } else if (videoRef.current) {
      videoRef.current.muted = false;
      setIsPlaying(true);
      startPlaying();
    }
  };

  useEffect(() => {
    const list = Array.isArray(videos) ? videos : [];
    const updateVideoSrc = () => {
      const selectedVideo =
        window.innerWidth < 1000
          ? list.find((video: { type: string }) => video.type === "mobile")
          : list.find((video: { type: string }) => video.type === "desktop");

      if (selectedVideo) {
        setVideoSrc(`${API_BASE}${selectedVideo.url}`);
      }
    };

    updateVideoSrc();
    window.addEventListener("resize", updateVideoSrc);

    return () => {
      window.removeEventListener("resize", updateVideoSrc);
    };
  }, [videos]);

  useEffect(() => {
    if (!videoSrc) {
      return;
    }
    const id = requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
    return () => cancelAnimationFrame(id);
  }, [videoSrc]);

  useLayoutEffect(() => {
    if (!containerRef.current || !videoRef.current) {
      return undefined;
    }

    const ctx = gsap.context(() => {
      gsap.to(videoRef.current, {
        scrollTrigger: {
          trigger: containerRef.current,
          start: "-=100px center",
          end: "bottom bottom",
          scrub: true,
          invalidateOnRefresh: true,
        },
        scale: 1,
      });
    });

    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });

    return () => ctx.revert();
  }, []);

  return (
    <section
      className={` container ${styles.sectionContainer}`}
      ref={containerRef}
    >
      <button
        aria-label="Sound control"
        className={`${styles.videoContainer}`}
        onClick={handleClick}
      >
        <video
          autoPlay
          playsInline
          ref={videoRef}
          loop
          muted
          className="video"
          data-hover="video"
          width="414"
          height="702"
          src={videoSrc}
        />
      </button>
    </section>
  );
};
